import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { post } from '@util/xhrAPI'
import { updateSterilizationCallFormObject, clearSterilizationCallForm, updateErrors, updateSterilizationPayment } from "@store/slice/sterilizationSlice"
import { updateNotification, updateValidationPage } from '@store/slice/mainSlice'
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { ROLE_PUBLIC } from "@router/Routes"
import useMainData from '@hook/useMainData'
import useStoreUpdate from "@hook/useStoreUpdate"
import useToggle from "@hook/useToggle"
import useFormData from "@hook/useFormData"
import useHtmlRenderer from '@hook/useHtmlRenderer'
import useGoogleRecaptchaTokenizer from "@hook/useGoogleRecaptchaTokenizer"
import useCostCalculator from "@hook/useCostCalculator"
import Div from '@ui/layout/Div'
import Form from '@ui/elements/Form'
import Buttons from '@ui/elements/Buttons'
import Alert from '@ui/elements/Alert'
import Modal from '@ui/component/Modal'
import sterilizationCitizenCallFormFields, { sterilizationCitizenCallSaveUrl } from "@form/sterilizationCitizenCallFormFields"
import GeocodeAddress from "@shared/GeocodeAddress"

const CitizenSterilizationCallMaker = () => {
  const { sterilizationCitizenCallFormObject, errors } = useSelector(state => state.sterilization)
  const { data, mission, missionBillings, rgpdPage } = useSelector(state => state.main)
  const { paymentConfig } = mission
  const [userCoordinates, setUserCoordinates] = useState(null)
  const [currentAddress, setCurrentAddress] = useState(null)
  const [cost, setCost] = useState(null)
  const [unitsToBill, setUnitsToBill] = useState(null)
  const dispatch = useDispatch()
  const loadData = useMainData()
  const calculateCost = useCostCalculator()
  const [isModalActive, toggleModal] = useToggle(false)
  const [updateFormObject, updateFormObjectProp] = useStoreUpdate(dispatch, updateSterilizationCallFormObject, sterilizationCitizenCallFormObject)
  const [isLoading, toggleLoading] = useToggle(false)
  const [t] = useTranslation(['sterilization', 'common'])
  const renderFormData = useFormData()
  const navigate = useNavigate()
  const renderHtml = useHtmlRenderer()
  const [invokeChallenge, appendScript] = useGoogleRecaptchaTokenizer()
  appendScript()

  const checkMyAddress = (validateAddress = false) => {
    toggleModal()
    if (true === validateAddress) {
      updateFormObjectProp('addressValidated', true)
    }
    if (false === validateAddress) {
      dispatch(updateSterilizationCallFormObject({
        ...sterilizationCitizenCallFormObject,
        'coordinates': null,
        sterilizationDistrict: '',
        sterilizationZipCode: '',
        sterilizationAddress: ''
      }))
    }
  }

  const renderCost = () => {
    if (!cost) return <></>
    return <>
      <Alert
        message={t('form.nests_number_help', { nestsNumber: sterilizationCitizenCallFormObject.nestsNumber, cost: cost })}
        type='is-info'
      />
    </>
  }

  const generateFormStep = step => {
    return sterilizationCitizenCallFormFields(
      step,
      sterilizationCitizenCallFormObject,
      updateFormObject,
      updateFormObjectProp,
      errors,
      saveForm,
      checkMyAddress,
      1 === parseInt(paymentConfig['defaultPayment']?.isBilled),
      isLoading,
      t,
      data,
      cost,
      renderCost
    )
  }

  const fetchInitialData = () => {
    const dataObject = {
      cities: 'cities',
      districts: 'districts'
    }
    loadData(dataObject)
  }

  const saveForm = async () => {
    toggleLoading()
    const token = await invokeChallenge(updateFormObjectProp)
    const callObject = { ...sterilizationCitizenCallFormObject, 'g-recaptcha-response': token }
    post(sterilizationCitizenCallSaveUrl, renderFormData(callObject, ['userCoordinates', 'coordinates']), response => {
      const { validationPage, sterilizationOrder, paymentModes, paymentPage, code, errors } = response
      if (200 === code) {
        dispatch(updateNotification({ type: 'is-success', message: t('messages.call_created_successfuly') }))
        dispatch(updateValidationPage(validationPage))
        dispatch(clearSterilizationCallForm())
        if (sterilizationOrder && sterilizationOrder.billedAmount) {
          dispatch(updateSterilizationPayment({ sterilizationOrder, paymentModes, paymentPage }))
          navigate(ROLE_PUBLIC['paymentPage'])
          window.localStorage.setItem('sterilizationOrder', sterilizationOrder.reference)
        } else {
          navigate(ROLE_PUBLIC['publicValidation'])
        }
      } else {
        if (!Array.isArray(errors)) {
          console.log(errors)
          dispatch(updateErrors(errors))
        }
        dispatch(updateNotification({ type: 'is-danger', message: t(`messages.${errors.length ? errors[0] : 'errors_encountered'}`) }))
      }

      toggleLoading()
    })
  }

  const updateUserCoordinates = () => {
    if (window.innerWidth <= 768) { //get user location if on mobile
      navigator.geolocation.getCurrentPosition(function (position) {
        setUserCoordinates({ lat: position.coords.latitude, lng: position.coords.longitude })
      })
    }
  }

  useEffect(() => {
    updateFormObjectProp('mission', mission.id)
    fetchInitialData()
    updateUserCoordinates()
  }, [])

  useEffect(() => {
    if (userCoordinates) {
      updateFormObjectProp('userCoordinates', userCoordinates)
    }
  }, [userCoordinates])

  useEffect(() => {
    const { sterilizationDistrict, sterilizationZipCode, sterilizationAddress } = sterilizationCitizenCallFormObject
    if (sterilizationDistrict && sterilizationZipCode && sterilizationAddress) {
      const selectedDistrict = data.districts.filter(district => district.id != sterilizationCitizenCallFormObject.sterilizationDistrict)[0]
      const writtenAddress = `${selectedDistrict.label}+${sterilizationCitizenCallFormObject.sterilizationZipCode}+${sterilizationCitizenCallFormObject.sterilizationAddress}`
      setCurrentAddress(writtenAddress)
    }

    if (
      1 === parseInt(paymentConfig['defaultPayment']?.isBilled)
      && sterilizationCitizenCallFormObject.nestsNumber
      && sterilizationCitizenCallFormObject.nestsNumber != unitsToBill
    ) {
      setCost(calculateCost(
        parseFloat(sterilizationCitizenCallFormObject.nestsNumber),
        Object.values(missionBillings).filter(billing => {
          return paymentConfig.defaultPayment.isSurfaceBased ? billing.isSurfaceBased : !billing.isSurfaceBased
        }),
        sterilizationCitizenCallFormObject.callType
      ))
      setUnitsToBill(sterilizationCitizenCallFormObject.nestsNumber)
    }

  }, [sterilizationCitizenCallFormObject])

  return (
    <>
      <Div cssCalss='sterilization-request-maker'>
        <h2 className="scene-title">{t('titles.citizen_sterilization_call_maker')}</h2>
        <Form cssClass='citizen-sterilization-call columns'>
          <Div cssClass='left-form column is-6'>
            <Div cssClass='columns is-multiline'>
              {generateFormStep('caller')}
            </Div>
          </Div>
          <Div cssClass='right-form column is-6'>
            <Div cssClass='columns is-multiline'>
              {generateFormStep('call')}
            </Div>
          </Div>
        </Form>
        <Div cssCalss='cms-content'>
          {renderHtml(rgpdPage.description)}
        </Div>
      </Div>
      <Modal cssClass='confirm-address' isActive={isModalActive} closeCallback={toggleModal}>
        <GeocodeAddress address={currentAddress} callback={updateFormObjectProp} />
        <Buttons buttons={[
          {
            icon: 'icon-done',
            text: t('common:actions.validate'),
            callback: () => checkMyAddress(true),
            cssClass: 'is-success'
          },
          {
            icon: 'icon-keyboard_arrow_left',
            text: t('common:actions.cancel'),
            callback: () => checkMyAddress(false),
            cssClass: 'is-danger'
          }
        ]} />
      </Modal>
    </>
  )
}

export default CitizenSterilizationCallMaker